import { useEffect, useReducer, useRef, type ReactNode } from 'react';
import { US_PATH, pseudoPosition } from '@/lib/us-map';
import { toMapXY } from '@/components/viz';
import {
  type Deal,
  PIPELINE_STATUS_COLORS,
  PIPELINE_STATUS_HEX,
} from '@/types';
import { type PipelineStatus } from '@arcanum-hq/shared-types';
import { Skeleton, EmptyState } from '@/components/ui';

// ---- Constants ----

const VW = 560;
const VH = 340;
const GRID_SPACING = 28; // Lovable Pipeline Map crosshatch spacing (research § 4.9 verbatim)

/**
 * CSS `var()` form for plain-colour sites (fill=, color:, stroke=).
 * Single-token references resolve correctly in these contexts.
 *
 * conversation-model-versioning Phase 5 Session 7 (2026-05-17):
 * replaced DEAL_STATUS_COLORS (4-value DealStatus) with 6-value PipelineStatus map.
 */
const PIPELINE_STATUS_COLORS_MAP: Record<PipelineStatus, string> =
  PIPELINE_STATUS_COLORS;

// PIPELINE_STATUS_HEX lifted to @/types/index.ts (2026-05-18) — imported above.
// DealPinMap still uses it for alpha-concat inline styles (${shx}33 border, etc.).

// ---- Types ----

/**
 * Wire shape for the `arcanum:pipeline-row-highlight` window event. Exported
 * so consumers (emitters in `<PipelineMap>`, listeners elsewhere) can share
 * the same `as CustomEvent<PipelineRowHighlightDetail>` cast at the
 * subscribe/dispatch boundary — per research § 9 (no module-augmentation in
 * tree for `arcanum:*` events; inline structural casts at each site).
 */
export type PipelineRowHighlightDetail = {
  dealId: string | null;
  source: 'row' | 'pin';
};

interface DealPinMapProps {
  deals: Deal[];
  isLoading?: boolean;
  error?: Error | null;
  activeDealId?: string | null;
  onPinHover?: (dealId: string | null) => void;
  onPinClick?: (dealId: string) => void;
  footerVariant: 'stat-row' | 'none';
  footerNode?: ReactNode;
  className?: string;
  /**
   * When true, subscribes to the `arcanum:pipeline-row-highlight` window
   * event with a `source==='row'` filter (self-source `'pin'` events are
   * ignored). The subscriber drives the internal active-dealId via a ref
   * + `useReducer` no-op tick — `activeDealId` prop is IGNORED while
   * subscribed. Opt-in: cockpit `<PipelineMap>` passes `true` for
   * bidirectional hover with `<PipelineSummary>`; pipeline-route
   * `<DealMap>` passes `false` (default) and keeps the prop-controlled path.
   */
  subscribeToHighlightEvents?: boolean;
  /**
   * Controls the empty-state (deals.length === 0) render:
   *  - `'card'` (default) — renders a centered EmptyState card (cockpit path).
   *  - `'bare'` — renders the map shell (US path + grid) with zero pins.
   *    The pipeline route passes `'bare'` so the 3-zone layout stays intact
   *    when there are no deals matching the active filter (C1 invariant).
   */
  emptyVariant?: 'card' | 'bare';
}

// ---- Component ----

export function DealPinMap({
  deals,
  isLoading = false,
  error = null,
  activeDealId = null,
  onPinHover,
  onPinClick,
  footerVariant,
  footerNode,
  className,
  subscribeToHighlightEvents = false,
  emptyVariant = 'card',
}: DealPinMapProps) {
  // ---- Window-event subscription (opt-in) ----
  //
  // Pattern (c) per research § 11 R2 — `useRef<string | null>` holds the
  // active-deal id, `useReducer` no-op tick re-renders on transition. This
  // matches the `<SignalAnchorListener>` strict-mode-safe precedent
  // (research § 4.13): effect → cleanup → effect doesn't leak listeners
  // because the cleanup fn removes the listener; no `lastConsumedRef`
  // before the timer, no setTimeout inside the handler.
  const externalActiveDealId = activeDealId;
  const internalActiveDealIdRef = useRef<string | null>(null);
  const [, forceTick] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    if (!subscribeToHighlightEvents) return undefined;
    if (typeof window === 'undefined') return undefined;
    const handler = (e: Event): void => {
      const detail = (e as CustomEvent<PipelineRowHighlightDetail>).detail;
      if (!detail) return;
      if (detail.source === 'pin') return; // self-source guard
      if (internalActiveDealIdRef.current === detail.dealId) return; // no-op
      internalActiveDealIdRef.current = detail.dealId;
      forceTick();
    };
    window.addEventListener('arcanum:pipeline-row-highlight', handler);
    return () =>
      window.removeEventListener('arcanum:pipeline-row-highlight', handler);
  }, [subscribeToHighlightEvents]);

  const effectiveActiveDealId = subscribeToHighlightEvents
    ? internalActiveDealIdRef.current
    : externalActiveDealId;

  // LOADING branch
  if (isLoading) {
    return (
      <div className={`flex-1 flex flex-col min-h-0 ${className ?? ''}`}>
        <div className="flex-1 min-h-0">
          <Skeleton className="h-full w-full" />
        </div>
        {footerVariant === 'stat-row' && footerNode && (
          <div className="shrink-0 mt-1">{footerNode}</div>
        )}
      </div>
    );
  }

  // ERROR branch
  if (error) {
    return (
      <div className={`flex-1 flex flex-col min-h-0 ${className ?? ''}`}>
        <div className="flex-1 min-h-0">
          <EmptyState title="FAILED TO LOAD MAP" description={error.message} />
        </div>
      </div>
    );
  }

  // EMPTY branch — degraded-data state; footer still renders.
  // `emptyVariant='bare'` falls through to the SUCCESS branch so the map
  // shell (US path + grid) renders with zero pins — preserving the C1
  // 3-zone layout invariant on the pipeline route. `emptyVariant='card'`
  // (default) renders the EmptyState card (cockpit path — unchanged).
  if (deals.length === 0 && emptyVariant === 'card') {
    return (
      <div className={`flex-1 flex flex-col min-h-0 ${className ?? ''}`}>
        <div className="flex-1 min-h-0">
          <EmptyState
            title="NO DEALS YET"
            description="Add your first deal to populate the map"
          />
        </div>
        {footerVariant === 'stat-row' && footerNode && (
          <div className="shrink-0 mt-1">{footerNode}</div>
        )}
      </div>
    );
  }

  // SUCCESS branch (also handles emptyVariant='bare' with zero deals)
  const activeDeal = effectiveActiveDealId
    ? deals.find((d) => d.id === effectiveActiveDealId) ?? null
    : null;
  // Plain-color site — var() form resolves correctly for color:/stroke= uses
  const activeSc = activeDeal
    ? PIPELINE_STATUS_COLORS_MAP[activeDeal.status] || 'var(--color-text-dim)'
    : null;
  // Alpha-concat site — hex companion required (CSS cannot compose var()NN)
  const activeShx = activeDeal
    ? PIPELINE_STATUS_HEX[activeDeal.status] || '#586878' // @design-token resolves --color-status-passed (muted fallback)
    : null;

  return (
    <div className={`flex-1 flex flex-col min-h-0 ${className ?? ''}`}>
      <div
        className="relative flex-1 min-h-0 border border-panel-border rounded-[2px] overflow-hidden bg-void"
        style={{
          backgroundImage: `linear-gradient(to right, var(--color-accent-glow) 1px, transparent 1px), linear-gradient(to bottom, var(--color-accent-glow) 1px, transparent 1px)`,
          backgroundSize: `${GRID_SPACING}px ${GRID_SPACING}px`,
        }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${VW} ${VH}`}
          preserveAspectRatio="xMidYMid meet"
          overflow="visible"
          style={{ cursor: 'crosshair' }}
        >
          <g transform="translate(0, 10)">
            <path
              d={US_PATH}
              fill="#00c8530a" /* @design-token resolves --color-accent @ alpha 0a */
              stroke="var(--color-accent)"
              strokeWidth="1"
              opacity="0.35"
              style={{
                filter:
                  'drop-shadow(0 0 6px #00c85318)' /* @design-token resolves --color-accent @ alpha 18 */,
              }}
            />
            {deals.map((d) => {
              const [cx, cy] =
                d.lat != null && d.lng != null
                  ? toMapXY(d.lat, d.lng)
                  : pseudoPosition(d.id);
              // Plain-color site — var() form for fill= / stroke=
              const sc =
                PIPELINE_STATUS_COLORS_MAP[d.status] || 'var(--color-text-dim)';
              // Alpha-concat site — hex companion required (CSS cannot compose var()NN)
              const shx = PIPELINE_STATUS_HEX[d.status] || '#586878'; // @design-token resolves --color-status-passed (muted fallback)
              const isActive = effectiveActiveDealId === d.id;
              return (
                <g key={d.id}>
                  {isActive && (
                    <circle
                      cx={cx}
                      cy={cy}
                      r="16"
                      fill="none"
                      stroke={sc}
                      strokeWidth="1.2"
                      opacity="0.5"
                    >
                      <animate
                        attributeName="r"
                        from="8"
                        to="30"
                        dur="2s"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="opacity"
                        from="0.6"
                        to="0"
                        dur="2s"
                        repeatCount="indefinite"
                      />
                    </circle>
                  )}
                  <circle
                    data-deal-pin={d.id}
                    cx={cx}
                    cy={cy}
                    r={isActive ? 7.5 : 5}
                    fill={sc}
                    opacity={isActive ? 1 : 0.75}
                    onMouseEnter={() => onPinHover?.(d.id)}
                    onMouseLeave={() => onPinHover?.(null)}
                    onClick={() => onPinClick?.(d.id)}
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.4s cubic-bezier(0.2,0,0,1)',
                      filter: isActive
                        ? `drop-shadow(0 0 5px ${shx}) drop-shadow(0 0 10px ${shx}88)`
                        : 'none',
                    }}
                  />
                </g>
              );
            })}
          </g>
        </svg>
        {activeDeal && activeSc && activeShx && (
          <div
            className="absolute top-1.5 right-1.5 px-2 py-1 rounded-[2px] pointer-events-none max-w-[180px]"
            style={{
              background: '#020304dd', // @design-token resolves --color-void @ alpha dd
              border: `1px solid ${activeShx}33`,
              backdropFilter: 'blur(6px)',
            }}
          >
            <div
              className="font-mono text-[10px] font-bold tracking-[0.3px]"
              style={{ color: activeSc }}
            >
              {activeDeal.name}
            </div>
            <div className="font-mono text-[8px] text-text-muted mt-0.5 tracking-[0.4px]">
              {activeDeal.market ?? '—'} ·{' '}
              {activeDeal.units != null ? `${activeDeal.units}u` : '—'} ·{' '}
              {activeDeal.basis ?? '—'}
            </div>
            <div className="font-mono text-[8px] text-text-dim mt-px tracking-[0.4px]">
              IRR {activeDeal.irr ?? '—'} · DSCR {activeDeal.dscr ?? '—'}
            </div>
          </div>
        )}
      </div>
      {footerVariant === 'stat-row' && footerNode && (
        <div className="shrink-0 mt-1">{footerNode}</div>
      )}
    </div>
  );
}
